import { useState } from "react";
import { Button } from "@openup/shared/primitives/ui/button";
import { Textarea } from "@openup/shared/primitives/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@openup/shared/primitives/ui/alert-dialog";
import FormField from "@src/components/spaces/FormField";

interface CancelDialogProps {
  open: boolean;
  /** e.g. "Cancel event" or "Cancel session". */
  title: string;
  /** What will happen, spelled out — sessions affected, attendees emailed. */
  description: string;
  pending: boolean;
  onConfirm: (reason: string | null) => void;
  onClose: () => void;
}

/**
 * Destructive-action confirmation for OI-24717 cancellations. The reason is
 * optional and lands verbatim in the attendees' cancellation email
 * (`cancellationAdditionalInformation`); the email template has fallback copy
 * when it is left blank.
 */
const CancelDialog = ({
  open,
  title,
  description,
  pending,
  onConfirm,
  onClose,
}: CancelDialogProps) => {
  const [reason, setReason] = useState("");

  const close = () => {
    setReason("");
    onClose();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next: boolean) => !next && !pending && close()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <FormField label="Reason">
          {({ id }) => (
            <Textarea
              id={id}
              rows={3}
              maxLength={1000}
              value={reason}
              placeholder="Optional — included in the cancellation email attendees receive"
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </FormField>

        {/* Layout classes repeated here so Tailwind generates them — the shared
            primitive's own classes aren't scanned by this app's build. */}
        <AlertDialogFooter className="flex-row justify-end">
          <Button variant="ghost" onClick={close} disabled={pending}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            // The shared `destructive` token is Tailwind-v4-shaped and resolves to
            // invalid CSS under this app's v3 preset — pin the palette explicitly.
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={pending}
            onClick={() => onConfirm(reason.trim() || null)}
          >
            {pending ? "Cancelling…" : title}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelDialog;
